/**
 * Pagina "Connessioni broker" (SPEC-011 §8).
 *
 * Due punti in cui la UI deve essere esplicita invece che comoda:
 *
 *  1. **I segreti non tornano mai indietro.** Il backend non li espone in
 *     nessun DTO, quindi non esiste un modo di "rivedere" una chiave: si
 *     sostituisce la connessione. La pagina lo dice, invece di lasciar cercare
 *     un pulsante che non c'è.
 *  2. **L'invio ordini è un consenso separato**, disattivo alla creazione, e su
 *     ambiente live richiede anche una configurazione del server. `canPlaceOrders`
 *     arriva già calcolato dal backend: la UI non ricostruisce quella regola.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { formatDateTime } from '@/lib/format';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Button, Card, DomainBadges, EmptyState, Field, Select, Spinner, Switch } from '@/ui';
import {
  useCancelExchangeOrder,
  useConnections,
  useCreateBitpandaConnection,
  useCreateTrading212Connection,
  useDeleteConnection,
  useExchangeOpenOrders,
  useResetResync,
  useSyncConnection,
  useUpdateConnection,
  useVerifyConnection,
  type BrokerConnection,
  type BrokerEnvironment,
  type ConnectionKind,
  type ExchangeProvider,
  type SyncRun,
} from './api';
import './connectors.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

function StatusBadge({ connection }: { connection: BrokerConnection }) {
  if (connection.status === 'error') return <Badge tone="negative">Credenziali da verificare</Badge>;
  if (connection.status === 'revoked') return <Badge tone="neutral">Revocata</Badge>;
  return <Badge tone="positive">Attiva</Badge>;
}

function EnvironmentBadge({ environment }: { environment: BrokerEnvironment }) {
  return environment === 'live' ? (
    <Badge tone="accent">Denaro reale</Badge>
  ) : (
    <Badge tone="neutral">Demo</Badge>
  );
}

const PROVIDER_LABEL: Record<string, string> = {
  trading212: 'Trading 212',
  bitpanda: 'Bitpanda Fusion',
  bitpanda_public: 'Bitpanda',
};

/** Il provider, detto in chiaro: due connessioni si distinguono prima dal nome del broker che dall'etichetta. */
function ProviderBadge({ provider }: { provider: string }) {
  return <Badge tone="neutral">{PROVIDER_LABEL[provider] ?? provider}</Badge>;
}

/**
 * Il riepilogo di una sincronizzazione. `partial` merita una frase e non solo
 * un'etichetta: senza spiegazione sembrerebbe un fallimento, mentre è lo stato
 * normale finché lo storico del broker non è stato scaricato tutto.
 */
function SyncSummary({ run, kind }: { run: SyncRun; kind: ConnectionKind }) {
  if (run.status === 'failed') {
    return <Alert variant="error" title="Sincronizzazione fallita">{run.error ?? 'Errore non specificato'}</Alert>;
  }

  const counts = `${run.positionsSynced} posizioni · ${run.transactionsCreated} movimenti nuovi · ${run.transactionsSkipped} già presenti`;

  if (run.status === 'partial' && run.hasMore) {
    return (
      <Alert variant="info" title="Sincronizzazione in corso">
        {counts}.{' '}
        {kind === 'crypto_exchange'
          ? "Lo storico dei trade si scarica poche pagine per volta: la sincronizzazione riprende da sola fino a completarsi, e solo allora la cassa viene riconciliata con il saldo dell'exchange."
          : 'Lo storico del broker si scarica poche pagine per volta (il suo limite è di 6 richieste al minuto): la sincronizzazione riprende da sola fino a completarsi.'}
      </Alert>
    );
  }

  return <Alert variant="info" title="Sincronizzazione completata">{counts}</Alert>;
}

// -----------------------------------------------------------------------------
// Nuova connessione
// -----------------------------------------------------------------------------

type Provider = 'trading212' | ExchangeProvider;

/**
 * Un selettore sopra i form: i provider hanno credenziali diverse, e un form
 * unico mentirebbe su uno di loro. Bitpanda compare DUE volte perche' sono
 * due API con due chiavi distinte: Fusion (l'exchange a coppie) e la Public
 * API (il conto Bitpanda, a quotazioni, con lo storico completo e anche
 * azioni, ETF e metalli).
 */
function NewConnectionForm() {
  const [provider, setProvider] = useState<Provider>('trading212');

  return (
    <div className="connectors__form">
      <Select
        label="Piattaforma da collegare"
        value={provider}
        onChange={(e) => setProvider(e.target.value as Provider)}
        options={[
          { value: 'trading212', label: 'Trading 212 (broker azionario)' },
          { value: 'bitpanda_public', label: 'Bitpanda — conto (Public API: crypto, azioni, ETF, metalli)' },
          { value: 'bitpanda', label: 'Bitpanda Fusion — exchange a coppie (Fusion API)' },
        ]}
      />
      {provider === 'trading212' ? <Trading212Form /> : <BitpandaForm provider={provider} />}
    </div>
  );
}

const EXCHANGE_FORM_COPY: Record<
  ExchangeProvider,
  { title: string; defaultLabel: string; subtitle: string; keyHint: string }
> = {
  bitpanda: {
    title: 'Collega Bitpanda Fusion',
    defaultLabel: 'Bitpanda Fusion',
    subtitle:
      'La chiave si genera da Bitpanda: Impostazioni account → API Keys → scheda Fusion. Serve lo scope Read; Trade solo se vuoi inviare ordini da qui.',
    keyHint:
      'Chiave Fusion, senza segreto. Cifrata prima di essere salvata e mai più visibile. Scade (massimo un anno): alla scadenza la connessione andrà ricollegata.',
  },
  bitpanda_public: {
    title: 'Collega il conto Bitpanda',
    defaultLabel: 'Bitpanda',
    subtitle:
      'La chiave si genera da Bitpanda: Impostazioni account → API Keys (chiave della Public API, DIVERSA da quella Fusion). Servono gli scope Balances e Transaction; Trade (Write) solo se vuoi comprare e vendere da qui.',
    keyHint:
      'Chiave della Public API, senza segreto. Cifrata prima di essere salvata e mai più visibile. Scade (massimo un anno). Il portafoglio conterrà tutto il conto: crypto, azioni, ETF, metalli.',
  },
};

/** Le valute fiat che Bitpanda quota: il backend rifiuta comunque quelle che l'exchange non conosce. */
const BITPANDA_QUOTE_CURRENCIES = ['EUR', 'USD', 'CHF', 'GBP'] as const;

/**
 * Bitpanda Fusion (SPEC-024): una sola chiave, nessun ambiente di prova, e la
 * valuta di conto scelta qui — e' la quote con cui si valutano le posizioni. Le
 * due cose che il form dice PRIMA, perche' non si scoprono dopo: e' denaro
 * reale per costruzione, e due chiavi dello stesso conto fanno due portafogli
 * (l'API non espone un id di conto con cui riconoscerlo).
 */
function BitpandaForm({ provider }: { provider: ExchangeProvider }) {
  const copy = EXCHANGE_FORM_COPY[provider];
  const create = useCreateBitpandaConnection(provider);
  const [label, setLabel] = useState(copy.defaultLabel);
  const [apiKey, setApiKey] = useState('');
  const [quoteCurrency, setQuoteCurrency] = useState<string>('EUR');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      { label: label.trim(), apiKey: apiKey.trim(), quoteCurrency },
      { onSuccess: () => setApiKey('') },
    );
  };

  return (
    <Card key={provider} title={copy.title} subtitle={copy.subtitle}>
      <form className="connectors__form" onSubmit={handleSubmit}>
        <Alert variant="warning" title="Denaro reale">
          Bitpanda non ha un ambiente di prova: la connessione opera sul conto vero. Gli ordini restano
          comunque bloccati finché non attivi il consenso qui sotto e il server non li autorizza.
        </Alert>
        <Field label="Nome della connessione" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} required />
        <Field
          label="API Key Bitpanda"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          hint={copy.keyHint}
          required
        />
        <Select
          label="Valuta di conto"
          value={quoteCurrency}
          onChange={(e) => setQuoteCurrency(e.target.value)}
          options={BITPANDA_QUOTE_CURRENCIES.map((code) => ({ value: code, label: code }))}
          hint="La valuta con cui si valutano le posizioni e si leggono i prezzi. Due chiavi dello stesso conto creano due portafogli: l'API non permette di riconoscerlo."
        />

        {create.isError && (
          <Alert variant="error" title="Collegamento non riuscito">
            {errorMessage(create.error, 'Impossibile collegare il conto')}
          </Alert>
        )}

        <Button type="submit" loading={create.isPending}>
          Verifica e collega
        </Button>
      </form>
    </Card>
  );
}

function Trading212Form() {
  const create = useCreateTrading212Connection();
  const [label, setLabel] = useState('Trading 212');
  const [environment, setEnvironment] = useState<BrokerEnvironment>('demo');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      { label: label.trim(), environment, apiKey: apiKey.trim(), apiSecret: apiSecret.trim() },
      {
        onSuccess: () => {
          // I campi si svuotano subito: una chiave API non deve restare in un
          // input dopo essere stata usata.
          setApiKey('');
          setApiSecret('');
        },
      },
    );
  };

  return (
    <Card
      title="Collega un conto Trading 212"
      subtitle="Le chiavi si generano dall'app Trading 212, sezione API. Sono personali e legate all'ambiente in cui le crei."
    >
      <form className="connectors__form" onSubmit={handleSubmit}>
        <Field
          label="Nome della connessione"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={120}
          required
        />
        <Select
          label="Ambiente"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value as BrokerEnvironment)}
          options={[
            { value: 'demo', label: 'Demo (paper trading)' },
            { value: 'live', label: 'Live (denaro reale)' },
          ]}
          hint="Una chiave generata in demo non funziona su live, e viceversa."
        />
        <Field
          label="API Key"
          type="password"
          autoComplete="off"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          required
        />
        <Field
          label="API Secret"
          type="password"
          autoComplete="off"
          value={apiSecret}
          onChange={(e) => setApiSecret(e.target.value)}
          hint="Cifrato prima di essere salvato. Non sarà più visibile: per sostituirlo si ricollega il conto."
          required
        />

        {create.isError && (
          <Alert variant="error" title="Collegamento non riuscito">
            {errorMessage(create.error, 'Impossibile collegare il conto')}
          </Alert>
        )}

        <Button type="submit" loading={create.isPending}>
          Verifica e collega
        </Button>
      </form>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Svuota e riscarica
// -----------------------------------------------------------------------------

/** Parola da digitare per confermare. Un clic in più si dà per sbaglio, una parola no. */
const PAROLA_DI_CONFERMA = 'CANCELLA';

/**
 * Lo svuotamento del portafoglio collegato.
 *
 * La sincronizzazione normale è **incrementale**: un movimento già scritto non
 * viene mai riletto, quindi un dato sbagliato resta sbagliato per sempre —
 * finora ogni riparazione del genere è costata una patch SQL. Questo è il
 * rimedio generale, ed è distruttivo: la pagina lo dice **prima**, con le
 * conseguenze in chiaro invece che con un generico «sei sicuro?».
 *
 * ⚠️ Le due conseguenze che non si annullano stanno in cima all'elenco: i
 * movimenti registrati a mano e gli snapshot giornalieri **non tornano**. Il
 * broker pubblica le posizioni di adesso, non il valore del portafoglio di
 * ieri: la curva dell'andamento riparte dal giorno del reset.
 */
function ResetResyncPanel({ connection }: { connection: BrokerConnection }) {
  const reset = useResetResync();
  const [aperto, setAperto] = useState(false);
  const [conferma, setConferma] = useState('');

  const chiudi = () => {
    setAperto(false);
    setConferma('');
  };

  if (reset.isSuccess) {
    const { deleted, run } = reset.data;
    const aMano = deleted.transactions - deleted.fromBroker;
    return (
      <div className="connectors__danger">
        <Alert variant="warning" title="Portafoglio svuotato e riscaricato">
          Cancellati {deleted.transactions} movimenti e {deleted.snapshots} snapshot giornalieri.
          {aMano > 0 && ` ${aMano} non venivano dal broker e non torneranno.`} Il riscaricamento ha
          già riportato {run.transactionsCreated} movimenti e {run.positionsSynced} posizioni
          {run.hasMore && ', e prosegue da solo con le pagine restanti'}.
        </Alert>
      </div>
    );
  }

  return (
    <div className="connectors__danger">
      {!aperto ? (
        <>
          <p className="connectors__danger-hint">
            La sincronizzazione non riscrive un movimento già noto: se un dato è entrato sbagliato,
            resta. Questo lo butta via tutto e lo riscarica.
          </p>
          <Button variant="secondary" onClick={() => setAperto(true)}>
            Svuota e riscarica…
          </Button>
        </>
      ) : (
        <>
          <Alert
            variant="error"
            title={`Stai per cancellare il contenuto di «${connection.portfolio.name}»`}
            items={[
              'I movimenti registrati a mano in questo portafoglio vengono cancellati e NON tornano: il broker non li conosce.',
              'Gli snapshot giornalieri vengono cancellati e NON tornano: il broker pubblica le posizioni di adesso, non il valore di ieri. La curva «Andamento del valore» riparte da oggi.',
              'I movimenti sincronizzati dalla piattaforma tornano da soli, ma lo storico riparte da zero: si riscarica poche pagine per volta, quindi possono servire più giri.',
              'Restano: le credenziali, il registro delle sincronizzazioni, gli ordini inviati e le proposte di auto-trading.',
            ]}
          />
          {reset.isError && (
            <Alert variant="error" title="Svuotamento non riuscito">
              {errorMessage(reset.error, 'Impossibile svuotare il portafoglio')}
            </Alert>
          )}
          <Field
            label={`Per confermare, scrivi ${PAROLA_DI_CONFERMA}`}
            value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="connectors__actions">
            <Button variant="ghost" onClick={chiudi} disabled={reset.isPending}>
              Annulla
            </Button>
            <Button
              variant="danger"
              disabled={conferma.trim() !== PAROLA_DI_CONFERMA}
              loading={reset.isPending}
              onClick={() => reset.mutate(connection.id)}
            >
              Svuota e riscarica
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ordini aperti su un exchange (SPEC-024 §7.5)
// -----------------------------------------------------------------------------

/**
 * Solo per un exchange: gli ordini limite e stop restano aperti per giorni, e
 * annullarli riduce l'esposizione — per questo la cancellazione NON richiede il
 * consenso agli ordini, come per Trading 212.
 */
function ExchangeOpenOrders({ connectionId }: { connectionId: string }) {
  const orders = useExchangeOpenOrders(connectionId);
  const cancel = useCancelExchangeOrder();

  if (orders.isLoading) return <Spinner label="Lettura degli ordini aperti…" />;
  if (orders.isError) {
    return (
      <Alert variant="warning" title="Ordini aperti non leggibili">
        {errorMessage(orders.error, "Impossibile leggere gli ordini dall'exchange")}
      </Alert>
    );
  }
  const items = orders.data?.items ?? [];
  if (items.length === 0) return <p className="connectors__danger-hint">Nessun ordine aperto sull'exchange.</p>;

  return (
    <div className="connectors__trading">
      <strong>Ordini aperti sull'exchange</strong>
      {cancel.isError && (
        <Alert variant="error" title="Cancellazione non riuscita">
          {errorMessage(cancel.error, "Impossibile cancellare l'ordine")}
        </Alert>
      )}
      <ul className="connectors__orders">
        {items.map((order) => (
          <li key={order.id} className="connectors__order">
            <span>
              <Badge tone={order.side === 'buy' ? 'positive' : 'negative'}>{order.side === 'buy' ? 'Acquisto' : 'Vendita'}</Badge>{' '}
              {order.pair} · {order.type}
              {order.quantity ? ` · ${order.quantity}` : order.amount ? ` · ${order.amount}` : ''}
              {order.limitPrice ? ` @ ${order.limitPrice}` : ''}
              {order.triggerPrice ? ` (attivazione ${order.triggerPrice})` : ''} · <em>{order.statusRaw}</em>
            </span>
            <Button
              variant="ghost"
              loading={cancel.isPending && cancel.variables?.orderId === order.id}
              onClick={() => cancel.mutate({ connectionId, orderId: order.id })}
            >
              Annulla ordine
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Connessione esistente
// -----------------------------------------------------------------------------

function ConnectionCard({ connection }: { connection: BrokerConnection }) {
  const sync = useSyncConnection();
  const verify = useVerifyConnection();
  const update = useUpdateConnection();
  const remove = useDeleteConnection();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <Card
      title={connection.label}
      subtitle={`Portafoglio collegato: ${connection.portfolio.name}${connection.accountRef ? ` · conto ${connection.accountRef}` : ''}`}
      action={
        <div className="connectors__badges">
          <ProviderBadge provider={connection.provider} />
          <DomainBadges domains={connection.domains} />
          <EnvironmentBadge environment={connection.environment} />
          <StatusBadge connection={connection} />
        </div>
      }
    >
      <dl className="connectors__meta">
        <div>
          <dt>Chiave</dt>
          <dd>
            <code>{connection.credentialsHint}…</code>
          </dd>
        </div>
        <div>
          <dt>Valuta del conto</dt>
          <dd>{connection.accountCurrency ?? '—'}</dd>
        </div>
        <div>
          <dt>Ultima verifica</dt>
          <dd>{formatDateTime(connection.lastVerifiedAt)}</dd>
        </div>
        <div>
          <dt>Ultima sincronizzazione</dt>
          <dd>{formatDateTime(connection.lastSyncAt)}</dd>
        </div>
        <div>
          <dt>Storico</dt>
          <dd>{connection.historyComplete ? 'Completo' : 'Scaricamento in corso'}</dd>
        </div>
      </dl>

      {connection.lastError && (
        <Alert variant="warning" title="Ultimo problema segnalato">
          {connection.lastError}
        </Alert>
      )}

      <div className="connectors__trading">
        <Switch
          label="Consenti l'invio di ordini da LabInvestment"
          checked={connection.tradingEnabled}
          disabled={update.isPending}
          onChange={(checked) => update.mutate({ id: connection.id, tradingEnabled: checked })}
        />
        {connection.tradingEnabled && !connection.canPlaceOrders && (
          <Alert variant="warning" title="Consenso attivo ma ordini bloccati">
            {connection.kind === 'crypto_exchange'
              ? 'Un exchange opera sempre su denaro reale: serve anche l\'autorizzazione del server'
              : 'Su una connessione live serve anche l\'autorizzazione del server'}{' '}
            (<code>CONNECTORS_ALLOW_LIVE_TRADING</code>). Finché manca, gli ordini vengono rifiutati.
          </Alert>
        )}
      </div>

      {connection.kind === 'crypto_exchange' && connection.capabilities?.openOrders && (
        <ExchangeOpenOrders connectionId={connection.id} />
      )}
      {connection.kind === 'crypto_exchange' && connection.capabilities?.tradingModel === 'quotes' && (
        <p className="connectors__danger-hint">
          Questo conto negozia a quotazione immediata: nessun ordine resta aperto, e lo storico include versamenti,
          prelievi e ricompense.
        </p>
      )}

      {sync.isSuccess && <SyncSummary run={sync.data} kind={connection.kind} />}
      {sync.isError && (
        <Alert variant="error" title="Sincronizzazione non riuscita">
          {errorMessage(sync.error, 'Impossibile sincronizzare')}
        </Alert>
      )}
      {verify.isError && (
        <Alert variant="error" title="Verifica non riuscita">
          {errorMessage(verify.error, 'Impossibile verificare le credenziali')}
        </Alert>
      )}

      <div className="connectors__actions">
        <Button onClick={() => sync.mutate(connection.id)} loading={sync.isPending}>
          Sincronizza ora
        </Button>
        <Button variant="secondary" onClick={() => verify.mutate(connection.id)} loading={verify.isPending}>
          Verifica credenziali
        </Button>
        {confirmingDelete ? (
          <>
            <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
              Annulla
            </Button>
            <Button variant="secondary" onClick={() => remove.mutate(connection.id)} loading={remove.isPending}>
              Conferma scollegamento
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
            Scollega
          </Button>
        )}
      </div>

      {confirmingDelete && (
        <Alert variant="info" title="Cosa succede scollegando">
          Le credenziali vengono cancellate. Il portafoglio «{connection.portfolio.name}» e i movimenti già
          sincronizzati restano: sono dati tuoi.
        </Alert>
      )}

      <ResetResyncPanel connection={connection} />
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Pagina
// -----------------------------------------------------------------------------

export function ConnectorsPage() {
  const connections = useConnections();

  return (
    <div className="connectors">
      <header className="connectors__header">
        <div>
          <h1 className="connectors__title">Connessioni broker</h1>
          <p className="connectors__subtitle">
            Un portafoglio che si aggiorna da solo dall'API del broker, invece che da un CSV caricato a mano.
          </p>
        </div>
      </header>

      {connections.isLoading && <Spinner label="Caricamento delle connessioni…" />}

      {connections.isError && (
        <Alert variant="error" title="Impossibile leggere le connessioni">
          {errorMessage(connections.error, 'Errore sconosciuto')}
        </Alert>
      )}

      {connections.data && connections.data.items.length === 0 && (
        <EmptyState
          icon="🔌"
          title="Nessun conto collegato"
          description="Collega un conto Trading 212 (posizioni, ordini, dividendi e movimenti di cassa) o un conto Bitpanda (saldi e trade crypto, con scambi crypto→crypto)."
        />
      )}

      <div className="connectors__grid">
        {connections.data?.items.map((connection) => (
          <ConnectionCard key={connection.id} connection={connection} />
        ))}
        <NewConnectionForm />
      </div>
    </div>
  );
}
